import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import LinkedIn from '@mui/icons-material/LinkedIn';
import FormatQuote from '@mui/icons-material/FormatQuote';
import { teamMemberService } from '../services/api';
import { getImageUrl } from '../utils/assets';
import SimplePageBanner from '../components/SimplePageBanner';

const BRAND_GREEN = '#477C2E';
const CREAM = '#F9F4E8';
const WARM_BG = '#FAF9F6';

function ContactRow({ member }) {
  if (!member.email && !member.phone && !member.linkedin_url) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1.5 }}>
      {member.email && (
        <IconButton size="small" component="a" href={`mailto:${member.email}`} sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <EmailOutlined fontSize="small" />
        </IconButton>
      )}
      {member.phone && (
        <IconButton size="small" component="a" href={`tel:${member.phone}`} sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <PhoneOutlined fontSize="small" />
        </IconButton>
      )}
      {member.linkedin_url && (
        <IconButton size="small" component="a" href={member.linkedin_url} target="_blank" rel="noopener noreferrer" sx={{ color: BRAND_GREEN, bgcolor: 'rgba(71,124,46,0.08)' }}>
          <LinkedIn fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

function ContactDetailsPanel({ member }) {
  const rows = [
    member.email
      ? {
          key: 'email',
          label: 'Email',
          value: member.email,
          href: `mailto:${member.email}`,
          icon: <EmailOutlined fontSize="small" />,
        }
      : null,
    member.phone
      ? {
          key: 'phone',
          label: 'Phone',
          value: member.phone,
          href: `tel:${member.phone}`,
          icon: <PhoneOutlined fontSize="small" />,
        }
      : null,
    member.linkedin_url
      ? {
          key: 'linkedin',
          label: 'LinkedIn',
          value: member.linkedin_url,
          href: member.linkedin_url,
          icon: <LinkedIn fontSize="small" />,
          external: true,
        }
      : null,
  ].filter(Boolean);

  if (!rows.length) {
    return (
      <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#9ca3af' }}>
        Contact details are not available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
      {rows.map((row) => (
        <Box
          key={row.key}
          component="a"
          href={row.href}
          target={row.external ? '_blank' : undefined}
          rel={row.external ? 'noopener noreferrer' : undefined}
          sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            p: 1.2,
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(249,250,251,0.95))',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(71,124,46,0.35)',
              boxShadow: '0 8px 24px rgba(17,24,39,0.08)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: BRAND_GREEN,
              bgcolor: 'rgba(71,124,46,0.1)',
              flexShrink: 0,
            }}
          >
            {row.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: '#6b7280', textTransform: 'uppercase' }}>
              {row.label}
            </Typography>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 14, fontWeight: 600, color: '#1f2937', wordBreak: 'break-word' }}>
              {row.value}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function FounderCard({ member, onReadMore }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${CREAM} 0%, #fff 55%, rgba(71,124,46,0.06) 100%)`,
        border: '1px solid rgba(71,124,46,0.12)',
        boxShadow: '0 12px 40px rgba(45,47,45,0.08)',
      }}
    >
      <Grid container>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              minHeight: { xs: 280, md: 420 },
              backgroundImage: member.photo_url ? `url(${getImageUrl(member.photo_url)})` : 'none',
              backgroundColor: '#e5e7eb',
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              position: 'relative',
            }}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <CardContent sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: 420 } }}>
            <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, mb: 2, borderRadius: 20, bgcolor: BRAND_GREEN, color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', width: 'fit-content' }}>
              Founder
            </Box>
            <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 28, md: 36 }, color: '#2D2929', mb: 1, lineHeight: 1.2 }}>
              {member.name}
            </Typography>
            {member.designation && (
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 16, color: '#6b7280', mb: 2 }}>
                {member.designation}
              </Typography>
            )}
            {member.bio && (
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 15, lineHeight: 1.75, color: '#4B4646', mb: 2 }}>
                {member.bio}
              </Typography>
            )}
            <ContactRow member={member} />
            <Button
              onClick={() => onReadMore(member)}
              variant="outlined"
              size="small"
              sx={{ mt: 2, borderColor: BRAND_GREEN, color: BRAND_GREEN, width: 'fit-content' }}
            >
              Read More
            </Button>
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );
}

function CoFounderCard({ member, onReadMore }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        textAlign: 'center',
        p: 3,
        bgcolor: '#fff',
        border: '1px solid #e8ecf1',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 16px 32px rgba(71,124,46,0.12)',
        },
      }}
    >
      <Avatar
        src={getImageUrl(member.photo_url) || undefined}
        alt={member.name}
        sx={{ width: 140, height: 140, mx: 'auto', mb: 2, border: '4px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', bgcolor: BRAND_GREEN, fontWeight: 800, fontSize: 44 }}
      >
        {member.name?.[0]}
      </Avatar>
      <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 800, fontSize: 22, color: '#2D2929' }}>
        {member.name}
      </Typography>
      {member.designation && (
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 14, color: BRAND_GREEN, mt: 0.5 }}>
          {member.designation}
        </Typography>
      )}
      {member.bio && (
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 14, color: '#6b7280', mt: 1.5, lineHeight: 1.65 }}>
          {member.bio.length > 160 ? `${member.bio.slice(0, 160)}…` : member.bio}
        </Typography>
      )}
      <ContactRow member={member} />
      <Button
        onClick={() => onReadMore(member)}
        variant="outlined"
        size="small"
        sx={{ mt: 2, borderColor: BRAND_GREEN, color: BRAND_GREEN }}
      >
        Read More
      </Button>
    </Card>
  );
}

const FoundersPage = () => {
  const [founders, setFounders] = useState([]);
  const [coFounders, setCoFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProfile, setOpenProfile] = useState(null);

  useEffect(() => {
    teamMemberService
      .getAll()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const data = Array.isArray(list) ? list : [];
        setFounders(data.filter((m) => m.role_type === 'founder'));
        setCoFounders(data.filter((m) => m.role_type === 'co_founder'));
      })
      .catch(() => {
        setFounders([]);
        setCoFounders([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const hasAny = founders.length > 0 || coFounders.length > 0;

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Vision & leadership"
        title="Founders"
        description="Meet the founders and co-founders guiding our mission and long-term impact."
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: BRAND_GREEN }} />
        </Box>
      ) : !hasAny ? (
        <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
          <FormatQuote sx={{ fontSize: 48, color: BRAND_GREEN, opacity: 0.4, mb: 2 }} />
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#6b7280' }}>
            Founder profiles will be published soon.
          </Typography>
        </Container>
      ) : (
        <>
          {founders.length > 0 && (
            <Box sx={{ bgcolor: WARM_BG, py: { xs: 6, md: 10 } }}>
              <Container maxWidth="lg">
                <Typography sx={{ textAlign: 'center', fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 26, md: 32 }, color: BRAND_GREEN, mb: 1 }}>
                  Guiding purpose
                </Typography>
                <Typography component="h2" sx={{ textAlign: 'center', fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 28, md: 40 }, color: '#2D2929', mb: 5 }}>
                  Founder
                </Typography>
                <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                  {founders.map((m) => (
                    <FounderCard key={m.id} member={m} onReadMore={setOpenProfile} />
                  ))}
                </Box>
              </Container>
            </Box>
          )}

          {coFounders.length > 0 && (
            <Box sx={{ bgcolor: CREAM, py: { xs: 6, md: 10 } }}>
              <Container maxWidth="lg">
                <Typography sx={{ textAlign: 'center', fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 26, md: 32 }, color: BRAND_GREEN, mb: 1 }}>
                  Partners in mission
                </Typography>
                <Typography component="h2" sx={{ textAlign: 'center', fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 28, md: 40 }, color: '#2D2929', mb: 5 }}>
                  Co-Founders
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                  {coFounders.map((m) => (
                    <Grid item xs={12} sm={6} md={coFounders.length === 1 ? 6 : 4} key={m.id}>
                      <CoFounderCard member={m} onReadMore={setOpenProfile} />
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={Boolean(openProfile)}
        onClose={() => setOpenProfile(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 24px 70px rgba(15,23,42,0.24)',
          },
        }}
      >
        {openProfile && (
          <>
            <DialogTitle
              sx={{
                py: 3,
                px: { xs: 2.5, md: 3.5 },
                background:
                  'linear-gradient(120deg, rgba(71,124,46,0.14) 0%, rgba(138,75,30,0.10) 100%)',
                borderBottom: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 24, md: 30 }, color: '#1f2937' }}>
                {openProfile.name}
              </Typography>
              {openProfile.designation && (
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: BRAND_GREEN, fontWeight: 600, mt: 0.5 }}>
                  {openProfile.designation}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Grid container>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      minHeight: { xs: 260, md: '100%' },
                      backgroundImage: `url(${getImageUrl(openProfile.photo_url) || ''})`,
                      backgroundColor: '#e5e7eb',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center top',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: '#fff' }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#4b5563', lineHeight: 1.85, whiteSpace: 'pre-wrap', fontSize: 15 }}>
                      {openProfile.bio || 'Profile details will be updated soon.'}
                    </Typography>
                    <Divider sx={{ my: 2.5 }} />
                    <ContactDetailsPanel member={openProfile} />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default FoundersPage;
